<template>
  <div class="backup-settings">
    <!-- 表格 -->
    <var-table v-if="backups.length > 0" class="backup-table" :scroller-height="'100%'" :elevation="0">
      <thead>
        <tr>
          <th>备份名称</th>
          <th>创建时间</th>
          <th v-if="can('system_restore_backup')" style="text-align:center; width: 220px;">操作</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="item in backups" :key="item.name">
          <td>
            <var-space :size="8">
              <var-icon name="file" size="18" color="#409eff" />
              <span>{{ item.name.replace(/\.sql$/, '') }}</span>
              <span style="color:var(--var-text-secondary);font-size:12px;">📦 {{ formatSize(item.size) }}</span>
            </var-space>
          </td>
          <td>{{ formatDate(item.time) }}</td>
          <td style="text-align:center;">
            <var-space v-if="can('system_restore_backup')" :size="4">
              <var-button size="small" type="success" plain @click="openRestoreDialog(item.name)">
                恢复
              </var-button>
              <var-button size="small" type="warning" plain @click="openRenameDialog(item.name)">
                编辑
              </var-button>
              <var-button size="small" type="danger" plain @click="openDeleteDialog(item.name)">
                删除
              </var-button>
            </var-space>
          </td>
        </tr>
      </tbody>
    </var-table>

    <!-- 空状态 -->
    <div v-else class="state-placeholder">
      <var-icon name="file-upload" size="48" color="#c0c4cc" />
      <span style="margin-top:8px;color:var(--var-text-secondary);">暂无备份</span>
    </div>

    <!-- 重命名弹窗 -->
    <var-dialog v-model:show="showRenameModal" :title="renameOldName ? '✏️ 重命名备份' : '➕ 创建备份'" width="440px"
      @confirm="handleRenameConfirm" :confirm-loading="renameLoading">
      <div style="padding:8px 0;">
        <div style="display:flex;flex-direction:column;gap:4px;margin-bottom:4px;">
          <span style="font-size:13px;color:#666;font-weight:500;">备份名称 <span style="color:#f56c6c;">*</span></span>
          <var-input v-model="renameName" placeholder="请输入备份名称" />
        </div>
        <div style="font-size:12px;color:#999;padding:0 4px;">💡 无需输入扩展名，自动添加 .sql</div>
      </div>
    </var-dialog>

    <!-- 确认恢复弹窗 -->
    <var-dialog v-model:show="showRestoreModal" title="⚠️ 确认恢复" width="440px" @confirm="handleRestoreConfirm"
      :confirm-loading="restoreLoading">
      <div style="padding:8px 0;">
        <p style="margin-bottom:8px;">确定要从备份 <strong>{{ restoreFileName }}</strong> 恢复数据吗？</p>
        <p style="color:#f56c6c;font-size:13px;">⚠️ 当前数据将被覆盖！</p>
      </div>
    </var-dialog>

    <!-- 确认删除弹窗 -->
    <var-dialog v-model:show="showDeleteModal" title="⚠️ 确认删除" width="440px" @confirm="handleDeleteConfirm"
      :confirm-loading="deleteLoading">
      <div style="padding:8px 0;">
        <p style="margin-bottom:8px;">确定要删除备份文件 <strong>{{ deleteFileName }}</strong> 吗？</p>
        <p style="color:#f56c6c;font-size:13px;">⚠️ 此操作不可恢复！</p>
      </div>
    </var-dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { Snackbar } from '@varlet/ui'
import { useAppStore } from '@/stores'
import { useFeature } from '@/composables/useFeature'
import { backupApi } from '@/api'
import { formatDate, formatSize } from '@/utils/helpers'

const appStore = useAppStore()

// ============================================================
// ✅ 使用 useFeature 获取权限
// ============================================================
const { can } = useFeature('system')

const backups = ref([])

// 重命名
const showRenameModal = ref(false)
const renameOldName = ref('')
const renameName = ref('')
const renameLoading = ref(false)

// 恢复
const showRestoreModal = ref(false)
const restoreFileName = ref('')
const restoreLoading = ref(false)

// 删除
const showDeleteModal = ref(false)
const deleteFileName = ref('')
const deleteLoading = ref(false)

async function loadBackups() {
  try {
    const data = await backupApi.list()
    console.log(data)
    if (data?.success) {
      backups.value = data.list || []
    }
  } catch (err) {
    console.error('[BackupSettings] 加载备份失败:', err)
    Snackbar.error('加载备份列表失败')
  }
}

// ===== 创建备份 =====
async function createBackup() {
  const defaultName = new Date().toISOString().replace(/[:-]/g, '').slice(0, 14)
  renameOldName.value = ''
  renameName.value = defaultName
  showRenameModal.value = true
}

async function handleRenameConfirm() {
  if (!renameName.value || !renameName.value.trim()) {
    Snackbar.error('请输入备份名称')
    return
  }

  renameLoading.value = true

  try {
    // 如果是创建模式（没有旧名称）
    if (!renameOldName.value) {
      Snackbar.info('正在创建备份...')
      const data = await backupApi.create(renameName.value.trim(), appStore.currentUser)
      if (data?.success) {
        Snackbar.success('✅ 备份创建成功')
        showRenameModal.value = false
        await loadBackups()
      }
      return
    }

    // 重命名模式
    await backupApi.rename(renameOldName.value, renameName.value.trim())
    Snackbar.success('重命名成功')
    showRenameModal.value = false
    await loadBackups()
  } catch (err) {
    Snackbar.error(renameOldName.value ? '重命名失败: ' + err.message : '备份创建失败: ' + err.message)
  } finally {
    renameLoading.value = false
  }
}

// ===== 恢复 =====
function openRestoreDialog(fileName) {
  restoreFileName.value = fileName
  showRestoreModal.value = true
}

async function handleRestoreConfirm() {
  restoreLoading.value = true
  try {
    const data = await backupApi.restore(restoreFileName.value)
    if (data?.success) {
      Snackbar.success('✅ 数据库恢复成功！')
      showRestoreModal.value = false
      setTimeout(() => window.location.reload(), 1500)
    }
  } catch (err) {
    Snackbar.error('恢复失败: ' + err.message)
  } finally {
    restoreLoading.value = false
  }
}

// ===== 重命名入口 =====
function openRenameDialog(fileName) {
  renameOldName.value = fileName
  renameName.value = fileName.replace(/\.sql$/, '')
  showRenameModal.value = true
}

// ===== 删除 =====
function openDeleteDialog(fileName) {
  deleteFileName.value = fileName
  showDeleteModal.value = true
}

async function handleDeleteConfirm() {
  deleteLoading.value = true
  try {
    await backupApi.delete(deleteFileName.value)
    Snackbar.success('删除成功')
    showDeleteModal.value = false
    await loadBackups()
  } catch (err) {
    Snackbar.error('删除失败: ' + err.message)
  } finally {
    deleteLoading.value = false
  }
}

defineExpose({ createBackup, loadBackups })

onMounted(() => {
  loadBackups()
})
</script>

<style scoped>
.backup-settings {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.backup-table {
  flex: 1;
}

.backup-table :deep(.var-table) {
  flex: 1;
  overflow: auto;
}

.state-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  gap: 4px;
  color: var(--var-text-secondary);
}
</style>